/**
 * ExpenseDetailPage Component
 * Shows full expense details, attachments, IRS category management, and edit/delete actions.
 * Route: /expenses/:id
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { expensesApi, expenseCategoriesApi } from '../lib/api';
import { readFileIntoMemory } from '@resida/shared';
import { useMarkSeen } from '../hooks/useAwareness';
import { PageHeader } from '../components/layout/index';
import { ExpensesLayout } from '../components/expenses/ExpensesLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import { ExpenseForm } from '../components/expenses/ExpenseForm';
import type {
  ExpenseDetail,
  ExpenseCategory,
  UpdateExpenseBody,
  CreateExpenseBody,
} from '../types/expense.types';
import { formatAmount, formatExpenseDate, formatFileSize } from '../utils/expense.utils';
import { formatDateTime } from '../utils/date';
import { formatUnitLabel, realUnitNumber } from '@resida/shared';

function SourceTypeBadge({ sourceType }: { sourceType: 'MANUAL' | 'EMAIL' | 'UPLOAD' }) {
  if (sourceType === 'EMAIL') return <Badge variant="secondary" className="bg-warning/10 text-warning">Email</Badge>;
  if (sourceType === 'UPLOAD') return <Badge variant="secondary">Upload</Badge>;
  return <Badge variant="secondary">Manual</Badge>;
}

function StatusBadge({ status }: { status: 'PROCESSING' | 'PENDING_REVIEW' | 'CONFIRMED' }) {
  if (status === 'CONFIRMED') return <Badge variant="secondary" className="bg-success/10 text-success" data-testid="expense-status-badge">Confirmed</Badge>;
  if (status === 'PENDING_REVIEW') return <Badge variant="secondary" className="bg-warning/10 text-warning" data-testid="expense-status-badge">Pending Review</Badge>;
  return <Badge variant="secondary" data-testid="expense-status-badge">Processing</Badge>;
}

export function ExpenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [expense, setExpense] = useState<ExpenseDetail | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [isAiAnalysisOpen, setIsAiAnalysisOpen] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [categoryOverride, setCategoryOverride] = useState<string>('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [categorySaveError, setCategorySaveError] = useState<string | null>(null);

  const [isRecategorizing, setIsRecategorizing] = useState(false);
  const [recategorizeError, setRecategorizeError] = useState<string | null>(null);

  const [isAddingAttachment, setIsAddingAttachment] = useState(false);
  const [attachmentUploadError, setAttachmentUploadError] = useState<string | null>(null);
  const [isDragOverAttachment, setIsDragOverAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [deleteAttachmentError, setDeleteAttachmentError] = useState<string | null>(null);

  const [sourceEmail, setSourceEmail] = useState<{
    from: string;
    subject: string;
    date: string | null;
    body: string;
  } | null>(null);
  const [isLoadingSourceEmail, setIsLoadingSourceEmail] = useState(false);
  const [sourceEmailError, setSourceEmailError] = useState<string | null>(null);
  const [sourceEmailLoaded, setSourceEmailLoaded] = useState(false);

  // AI-suggested names, derived from extractedData (see the effect below).
  const [aiPropertyName, setAiPropertyName] = useState<string | null>(null);
  const [aiUnitName, setAiUnitName] = useState<string | null>(null);
  const [aiAssignmentReasoning, setAiAssignmentReasoning] = useState<string | null>(null);
  const [aiCategorizationReasoning, setAiCategorizationReasoning] = useState<string | null>(null);

  const fetchExpense = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [expenseResult, categoriesResult] = await Promise.all([
        expensesApi.get(id),
        expenseCategoriesApi.list(),
      ]);
      setExpense(expenseResult);
      setCategories(categoriesResult.categories);
      setCategoryOverride(expenseResult.irsCategoryId ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load expense');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchExpense();
  }, [fetchExpense]);

  // BR-16 (docs/features/change-awareness/specification.md) — opening this expense's detail
  // page marks ITS awareness items seen, same as LeadDetailPage does for a lead. The review
  // queue (ExpenseReviewQueuePage) is a list page and deliberately does not mark seen — the
  // resolveNotificationPath table (notification-links.test.ts) routes expense_review_queued
  // to this instance-scoped `/expenses/<id>` route specifically so there's a real "open" to
  // hang the mark on, unlike agent_response_review_log, which has no per-item route.
  const markSeen = useMarkSeen();
  useEffect(() => {
    if (id) {
      markSeen.mutate({ referenceType: 'expense', referenceId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Derive the AI-suggested display values from extractedData.
  //
  // The CONFIRMED property/unit names are NOT resolved here — they arrive on the expense
  // itself as `propertyName` / `unitNumber`, already normalized server-side (the
  // single-family sentinel becomes null). The two getProperty()/getUnit() round-trips this effect used to
  // fire were redundant once that landed (GH #806).
  //
  // AI-suggested values stay on this path deliberately: the IDs are untrusted AI output and
  // may be hallucinated, so they must not be resolved through authz-gated API calls.
  useEffect(() => {
    if (!expense) return;

    const extracted = expense.extractedData as {
      assignment?: { propertyName?: string; unitNumber?: string; reasoning?: string };
      categorization?: { reasoning?: string };
    } | null;

    setAiPropertyName(extracted?.assignment?.propertyName ?? null);
    setAiUnitName(realUnitNumber(extracted?.assignment?.unitNumber));
    // GH #539: the model's own rationale for the property and category it picked.
    setAiAssignmentReasoning(extracted?.assignment?.reasoning ?? null);
    setAiCategorizationReasoning(extracted?.categorization?.reasoning ?? null);
  }, [expense]);

  const handleUpdateSubmit = async (values: UpdateExpenseBody) => {
    if (!id) return;
    setIsUpdating(true);
    // No local try/catch here (don't set the page-level `error` state on failure):
    // the Edit modal stays open on failure, and this page's error banner renders in
    // normal page flow behind the modal's `fixed inset-0` overlay, so it would never
    // be seen. Letting the error propagate lets ExpenseForm's own try/catch around
    // onSubmit catch it and render its error banner inside the still-open modal
    // instead — the only place a user submitting from the Property/Unit selectors
    // (AC-6 403 ownership, AC-8 400 unit/property mismatch) can actually see it.
    try {
      const result = await expensesApi.update(id, values);
      setExpense(result);
      setCategoryOverride(result.irsCategoryId ?? '');
      setIsEditModalOpen(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !expense) return;
    setIsDeleting(true);
    try {
      await expensesApi.delete(id);
      navigate(expense.propertyId ? `/properties/${expense.propertyId}` : '/expenses');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete expense');
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCategoryOverrideSave = async () => {
    if (!id) return;
    setIsSavingCategory(true);
    setCategorySaveError(null);
    try {
      const result = await expensesApi.update(id, { irsCategoryId: categoryOverride || undefined });
      setExpense(result);
      setCategoryOverride(result.irsCategoryId ?? '');
    } catch (err) {
      setCategorySaveError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleRecategorize = async () => {
    if (!id) return;
    setIsRecategorizing(true);
    setRecategorizeError(null);
    try {
      const result = await expensesApi.recategorize(id);
      setExpense(result);
      setCategoryOverride(result.irsCategoryId ?? '');
    } catch (err) {
      setRecategorizeError(err instanceof Error ? err.message : 'Failed to re-categorize');
    } finally {
      setIsRecategorizing(false);
    }
  };

  const handleAttachmentFile = useCallback(async (file: File) => {
    if (!id) return;
    setIsAddingAttachment(true);
    setAttachmentUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', await readFileIntoMemory(file));
      formData.append('attachmentType', 'OTHER');
      await expensesApi.addAttachment(id, formData);
      await fetchExpense();
    } catch (err) {
      setAttachmentUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsAddingAttachment(false);
    }
  }, [id, fetchExpense]);

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!id) return;
    setDeleteAttachmentError(null);
    try {
      await expensesApi.deleteAttachment(id, attachmentId);
      setDeletingAttachmentId(null);
      await fetchExpense();
    } catch (err) {
      setDeleteAttachmentError(err instanceof Error ? err.message : 'Failed to delete attachment');
    }
  };

  const handleShowSourceEmail = async () => {
    if (!id || sourceEmailLoaded) return;
    setIsLoadingSourceEmail(true);
    setSourceEmailError(null);
    try {
      const result = await expensesApi.getSourceEmailContent(id);
      setSourceEmail(result.email);
      setSourceEmailLoaded(true);
    } catch (err) {
      setSourceEmailError(
        err instanceof Error ? err.message : 'Source email not available'
      );
    } finally {
      setIsLoadingSourceEmail(false);
    }
  };

  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  const hasAiSuggestion =
    expense?.irsCategoryAiSuggested &&
    expense.irsCategoryAiSuggested !== expense.irsCategoryId;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !expense) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] mt-6">
          <div className="text-center py-16 px-6 bg-card rounded-lg border border-border">
            <h2 className="text-xl font-semibold text-foreground mb-3">Error Loading Expense</h2>
            <p className="text-sm text-destructive mb-6">{error}</p>
            <div className="flex justify-center gap-3">
              <Button size="sm" onClick={fetchExpense}>Retry</Button>
              <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Go Back</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] mt-6">
          <div className="text-center py-16 px-6 bg-card rounded-lg border border-border">
            <h2 className="text-xl font-semibold mb-3">Expense Not Found</h2>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  const isConfirmed = expense.status === 'CONFIRMED';

  // AI Analysis card: for PENDING_REVIEW / PROCESSING it renders fully expanded with
  // no toggle (as it always has); for CONFIRMED it becomes a collapsed-by-default
  // disclosure so a confirmed expense's page reads Details-first (AC-12..AC-15).
  function renderAiAnalysis() {
    // TypeScript does not carry the outer null-guard's narrowing of `expense` into a
    // nested function declaration — re-assert it here (always true at every call site,
    // both of which are below the guard above).
    if (!expense) return null;
    return (
      (expense.aiConfidence !== null || expense.irsCategoryAiSuggested !== null) && (
        <div
          className="px-[var(--page-padding-x)] mt-4"
          data-testid="ai-suggestions-section"
        >
          <div className="bg-accent/50 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="m-0 text-base font-semibold text-primary">
                AI Analysis
              </h2>
              {isConfirmed && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAiAnalysisOpen((v) => !v)}
                  aria-expanded={isAiAnalysisOpen}
                  aria-controls="ai-analysis-content"
                  data-testid="ai-analysis-toggle"
                  aria-label={isAiAnalysisOpen ? 'Hide AI Analysis details' : 'Show AI Analysis details'}
                >
                  {isAiAnalysisOpen ? 'Hide' : 'Show'}
                </Button>
              )}
            </div>
            {(!isConfirmed || isAiAnalysisOpen) && (
              <div id="ai-analysis-content">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm mb-3">
                  <div>
                    <dt className="text-primary text-xs mb-0.5">Property Suggested</dt>
                    <dd className="m-0 text-foreground font-medium">
                      {expense.aiPropertyIdSuggested
                        ? (aiPropertyName ?? 'Name unavailable')
                        : 'Not identified'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-primary text-xs mb-0.5">Unit Suggested</dt>
                    <dd className="m-0 text-foreground font-medium">
                      {expense.aiUnitIdSuggested
                        ? (formatUnitLabel(aiUnitName) ?? 'Name unavailable')
                        : 'Not identified'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-primary text-xs mb-0.5">Category Suggested</dt>
                    <dd className="m-0 text-foreground font-medium">
                      {expense.irsCategoryAiSuggested
                        ? (categories.find((c) => c.id === expense.irsCategoryAiSuggested)?.name ?? expense.irsCategoryAiSuggested)
                        : 'None'}
                    </dd>
                  </div>
                </dl>
                {/* GH #539: the model's stated rationale. Persisted on the expense in
                    `extractedData` at intake (both the email and upload paths), so a landlord
                    can see WHY a property or category was chosen rather than only how
                    confident the model was. Rendered as plain text — untrusted model output,
                    never interpreted as markup. */}
                {(aiAssignmentReasoning || aiCategorizationReasoning) && (
                  <dl
                    className="text-xs text-muted-foreground mb-3 space-y-1.5"
                    data-testid="ai-reasoning"
                  >
                    {aiAssignmentReasoning && (
                      <div>
                        <dt className="text-primary mb-0.5">Why this property</dt>
                        <dd className="m-0 italic" data-testid="ai-assignment-reasoning">
                          {aiAssignmentReasoning}
                        </dd>
                      </div>
                    )}
                    {aiCategorizationReasoning && (
                      <div>
                        <dt className="text-primary mb-0.5">Why this category</dt>
                        <dd className="m-0 italic" data-testid="ai-categorization-reasoning">
                          {aiCategorizationReasoning}
                        </dd>
                      </div>
                    )}
                  </dl>
                )}
                {expense.aiConfidence !== null && typeof expense.aiConfidence === 'object' && (
                  (() => {
                    const conf = expense.aiConfidence as Record<string, unknown>;
                    const pct = (k: string) => `${Math.round(((conf[k] as number) ?? 0) * 100)}%`;
                    return (
                      <div
                        className="text-xs text-primary"
                        data-testid="ai-confidence-overall"
                        aria-label="AI confidence scores"
                      >
                        <strong>Confidence:</strong> Overall {pct('overall')} | Property {pct('property')} | Category {pct('category')}
                      </div>
                    );
                  })()
                )}
                {expense.status === 'PENDING_REVIEW' && (
                  <div className="mt-3">
                    <Button asChild size="sm" aria-label="Go to expense review queue">
                      <Link to="/expenses/confirm">
                        Confirm this expense &rarr;
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )
    );
  }

  return (
    <ExpensesLayout>
    <div className="max-w-4xl mx-auto pb-6" data-testid="expense-detail">
      <div className="px-[var(--page-padding-x)]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="expense-back-button"
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
          <span>Back</span>
        </button>
        <PageHeader
          title={expense.description}
          subtitle={`${formatAmount(expense.amount)} · ${formatExpenseDate(expense.expenseDate)}${expense.vendorName ? ` · ${expense.vendorName}` : ''}`}
          action={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditModalOpen(true)} data-testid="expense-edit-button" aria-label="Edit expense">
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsDeleteDialogOpen(true)} data-testid="expense-delete-button" aria-label="Delete expense" className="text-destructive hover:text-destructive">
                Delete
              </Button>
            </div>
          }
        />
        <div className="flex items-center gap-2 mt-2">
          <span className="text-2xl font-bold text-foreground tabular-nums" data-testid="expense-detail-amount" aria-label={`Amount: ${formatAmount(expense.amount)}`}>
            {formatAmount(expense.amount)}
          </span>
          <SourceTypeBadge sourceType={expense.sourceType} />
          <StatusBadge status={expense.status} />
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="px-[var(--page-padding-x)] mt-4"
        >
          <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm">
            {error}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="px-[var(--page-padding-x)] mt-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="m-0 mb-3 text-base font-semibold text-foreground">
            Details
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {expense.propertyId && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Property</dt>
                <dd className="m-0 text-foreground font-medium">
                  <Link to={`/properties/${expense.propertyId}`} className="text-primary no-underline hover:text-primary">
                    {expense.propertyName ?? 'Property'}
                  </Link>
                </dd>
              </div>
            )}
            {/* A single-family expense has a unitId (the sentinel unit propertyService
                creates with its Property, from the unit details on that same create
                request) but no meaningful unit to name — its unitNumber is the
                single-family sentinel, normalized to null server-side. Omit the row entirely rather than
                render a bare "Unit" link, matching formatExpenseLocation()'s dashboard
                behaviour of showing the property name alone for single-family.
                Keyed on propertyType, NOT on unitNumber being null: a multi_family unit
                identified by street address rather than number also has a null unitNumber,
                and that unit IS a distinct place worth linking to (AC-15's six Details rows). */}
            {expense.unitId && expense.propertyType !== 'single_family' && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Unit</dt>
                <dd className="m-0 text-foreground font-medium">
                  <Link to={`/units/${expense.unitId}`} className="text-primary no-underline hover:text-primary">
                    {formatUnitLabel(expense.unitNumber) ?? 'Unit'}
                  </Link>
                </dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground mb-0.5">Scope</dt>
              <dd className="m-0 text-foreground font-medium">{expense.scope === 'UNIT' ? 'Unit' : 'Property'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Source</dt>
              <dd className="m-0"><SourceTypeBadge sourceType={expense.sourceType} /></dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Created</dt>
              <dd className="m-0 text-foreground">{formatDateTime(expense.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground mb-0.5">Last Updated</dt>
              <dd className="m-0 text-foreground">{formatDateTime(expense.updatedAt, { year: 'numeric', month: 'short', day: 'numeric' })}</dd>
            </div>
          </dl>
        </div>
      </div>

      {!isConfirmed && renderAiAnalysis()}

      {/* IRS Category Section */}
      <div className="px-[var(--page-padding-x)] mt-4">
        <div className="rounded-lg border border-border bg-card p-4" data-testid="expense-detail-category">
          <h2 className="m-0 mb-4 text-base font-semibold text-foreground">
            IRS Category
          </h2>

          {hasAiSuggestion && (
            <div
              className="p-3 mb-4 bg-accent/50 border border-border rounded-md text-sm text-primary"
              data-testid="expense-ai-suggestion"
              role="note"
              aria-label="AI category suggestion"
            >
              <strong>AI Suggestion:</strong> {categories.find((c) => c.id === expense.irsCategoryAiSuggested)?.name ?? expense.irsCategoryAiSuggested}
              {expense.irsCategoryId !== expense.irsCategoryAiSuggested && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-3"
                  onClick={() => setCategoryOverride(expense.irsCategoryAiSuggested ?? '')}
                  aria-label={`Accept AI suggestion: ${expense.irsCategoryAiSuggested}`}
                >
                  Accept Suggestion
                </Button>
              )}
            </div>
          )}

          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="expense-category-override" className="block text-xs font-medium text-foreground mb-1">
                Category
              </label>
              <select
                id="expense-category-override"
                value={categoryOverride}
                onChange={(e) => setCategoryOverride(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                data-testid="expense-category-override"
                aria-label="IRS expense category override"
              >
                <option value="">-- Uncategorized --</option>
                {sortedCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                    {cat.scheduleELine ? ` (Schedule E: ${cat.scheduleELine})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleCategoryOverrideSave}
              disabled={isSavingCategory || categoryOverride === (expense.irsCategoryId ?? '')}
              data-testid="expense-category-save"
              aria-label="Save category"
            >
              {isSavingCategory ? 'Saving...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRecategorize}
              disabled={isRecategorizing}
              data-testid="expense-recategorize-button"
              aria-label="Re-run AI categorization"
            >
              {isRecategorizing ? 'Running...' : 'Re-run AI'}
            </Button>
          </div>

          {categorySaveError && (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {categorySaveError}
            </p>
          )}
          {recategorizeError && (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {recategorizeError}
            </p>
          )}
        </div>
      </div>

      {/* Attachments Section */}
      <div className="px-[var(--page-padding-x)] mt-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="m-0 text-base font-semibold text-foreground">
              Attachments
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => attachmentInputRef.current?.click()}
              disabled={isAddingAttachment}
              data-testid="expense-add-attachment"
              aria-label="Add attachment"
            >
              {isAddingAttachment ? 'Uploading...' : '+ Add Attachment'}
            </Button>
            <input
              ref={attachmentInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
              className="hidden"
              aria-hidden="true"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  await handleAttachmentFile(file);
                  e.target.value = '';
                }
              }}
            />
          </div>

          {/* Drag-over area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragOverAttachment(true); }}
            onDragLeave={() => setIsDragOverAttachment(false)}
            onDrop={async (e) => {
              e.preventDefault();
              setIsDragOverAttachment(false);
              const file = e.dataTransfer.files[0];
              if (file) await handleAttachmentFile(file);
            }}
            className={`border-2 border-dashed rounded-md text-center text-xs text-muted-foreground flex items-center justify-center transition-all mb-3 ${
              isDragOverAttachment
                ? 'border-primary bg-accent/50 py-6 min-h-[80px]'
                : 'border-border bg-transparent py-2 px-3 min-h-[32px]'
            }`}
          >
            {isDragOverAttachment ? 'Drop file here' : 'Drop files here or click "Add Attachment"'}
          </div>

          {attachmentUploadError && (
            <p role="alert" className="m-0 mb-3 text-xs text-destructive">
              {attachmentUploadError}
            </p>
          )}
          {deleteAttachmentError && (
            <p role="alert" className="m-0 mb-3 text-xs text-destructive">
              {deleteAttachmentError}
            </p>
          )}

          {expense.attachments.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm" data-testid="expense-attachments-list">
              No attachments yet.
            </div>
          ) : (
            <div
              className="flex flex-col gap-2"
              data-testid="expense-attachments-list"
            >
              {expense.attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="p-3 bg-accent/50 rounded-md border border-border"
                >
                  {deletingAttachmentId === attachment.id ? (
                    <div>
                      <p className="m-0 mb-2 text-sm text-foreground">
                        Delete <strong>{attachment.fileName}</strong>? This cannot be undone.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <Button type="button" variant="outline" size="sm" onClick={() => { setDeletingAttachmentId(null); setDeleteAttachmentError(null); }}>
                          Cancel
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => handleDeleteAttachment(attachment.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="m-0 text-sm font-medium text-foreground truncate">
                          {attachment.fileName}
                        </p>
                        <p className="m-0 text-xs text-muted-foreground">
                          {formatFileSize(attachment.fileSize)} &bull; {attachment.attachmentType}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button asChild variant="outline" size="sm">
                          <a
                            href={`${expensesApi.attachmentDownloadUrl(expense.id, attachment.id)}?disposition=inline`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`attachment-view-button-${attachment.id}`}
                            aria-label={`View ${attachment.fileName}`}
                          >
                            View
                          </a>
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => { setDeletingAttachmentId(attachment.id); setDeleteAttachmentError(null); }}
                          aria-label={`Delete attachment: ${attachment.fileName}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Source Email Section */}
      {expense.sourceType === 'EMAIL' && (
        <div className="px-[var(--page-padding-x)] mt-4" data-testid="source-email-section">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="m-0 text-base font-semibold text-foreground">
                Source Email
              </h2>
              {!sourceEmailLoaded && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleShowSourceEmail}
                  disabled={isLoadingSourceEmail}
                  data-testid="source-email-show-button"
                  aria-label="Show source email content"
                >
                  {isLoadingSourceEmail ? 'Loading...' : 'Show'}
                </Button>
              )}
            </div>

            {sourceEmailError && (
              <p role="alert" data-testid="source-email-error" className="text-sm text-destructive">
                {sourceEmailError}
              </p>
            )}

            {sourceEmail && (
              <div data-testid="source-email-content">
                <dl className="grid grid-cols-1 gap-y-2 text-sm mb-3">
                  <div>
                    <dt className="text-muted-foreground text-xs mb-0.5">From</dt>
                    <dd className="m-0 text-foreground font-medium">{sourceEmail.from}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs mb-0.5">Subject</dt>
                    <dd className="m-0 text-foreground font-medium">{sourceEmail.subject}</dd>
                  </div>
                  {sourceEmail.date && (
                    <div>
                      <dt className="text-muted-foreground text-xs mb-0.5">Sent</dt>
                      <dd className="m-0 text-foreground">
                        {formatDateTime(sourceEmail.date, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </dd>
                    </div>
                  )}
                </dl>
                <dl className="grid grid-cols-1 gap-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground text-xs mb-1">Body</dt>
                    <dd className="m-0">
                      <pre
                        className="text-sm text-foreground whitespace-pre-wrap break-words bg-muted/30 rounded-md p-3 overflow-auto max-h-80 font-mono leading-relaxed"
                        data-testid="source-email-body"
                        aria-label="Source email body"
                      >
                        {sourceEmail.body || '(No text content)'}
                      </pre>
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes Section */}
      {expense.notes && (
        <div className="px-[var(--page-padding-x)] mt-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="m-0 mb-3 text-base font-semibold text-foreground">
              Notes
            </h2>
            <p className="m-0 text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {expense.notes}
            </p>
          </div>
        </div>
      )}

      {isConfirmed && renderAiAnalysis()}

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Expense"
        width="large"
      >
        <ExpenseForm
          mode="edit"
          propertyId={expense.propertyId ?? ''}
          unitId={expense.unitId ?? undefined}
          initialValues={expense}
          categories={categories}
          onSubmit={handleUpdateSubmit as (values: CreateExpenseBody | UpdateExpenseBody) => Promise<void>}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={isUpdating}
        />
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense (${formatAmount(expense.amount)})? This will also delete all attachments. This action cannot be undone.`}
        confirmText="Delete Expense"
        isDestructive
        isLoading={isDeleting}
      />
    </div>
    </ExpensesLayout>
  );
}

export default ExpenseDetailPage;
